import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useInitiate2FA, useVerify2FA, useDisable2FA } from '@/hooks/useSettings';
import styles from './TwoFAModal.module.css';

/**
 * TwoFAModal Component
 * Modal for enabling/disabling 2FA
 */
const TwoFAModal = ({ isOpen, onClose, twoFaEnabled }) => {
  const [verifyCode, setVerifyCode] = useState('');
  const [twoFaQrCode, setTwoFaQrCode] = useState(null);
  const [twoFaSecret, setTwoFaSecret] = useState(null);

  // React Query mutations
  const initiate2FAMutation = useInitiate2FA();
  const verify2FAMutation = useVerify2FA();
  const disable2FAMutation = useDisable2FA();

  useEffect(() => {
    if (isOpen && !twoFaEnabled) {
      handleInitiate2FA();
    } else {
      setTwoFaQrCode(null);
      setTwoFaSecret(null);
      setVerifyCode('');
    }
  }, [isOpen, twoFaEnabled]);

  const handleInitiate2FA = async () => {
    try {
      const data = await initiate2FAMutation.mutateAsync();
      if (data.qrCode && data.secret) {
        setTwoFaQrCode(data.qrCode);
        setTwoFaSecret(data.secret);
      }
    } catch (error) {
      // Error handled by mutation hook
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();

    if (!verifyCode) {
      return;
    }

    try {
      await verify2FAMutation.mutateAsync(verifyCode);
      setVerifyCode('');
      setTwoFaQrCode(null);
      setTwoFaSecret(null);
      onClose();
    } catch (error) {
      // Error handled by mutation hook
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();

    if (!verifyCode) {
      return;
    }

    try {
      await disable2FAMutation.mutateAsync(verifyCode);
      setVerifyCode('');
      onClose();
    } catch (error) {
      // Error handled by mutation hook
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalWrapper} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>
        
        <h3 className={styles.modalTitle}>
          {twoFaEnabled ? 'Manage 2FA' : 'Enable 2FA'}
        </h3>
        
        <div className={styles.modalBody}>
          {!twoFaEnabled && twoFaQrCode && (
            <div className={styles.qrCodeWrapper}>
              <img 
                src={twoFaQrCode} 
                alt="2FA QR Code" 
                className={styles.qrCodeImage}
              />
              <p className={styles.qrCodeText}>
                Scan this QR code with your authenticator app
              </p>
            </div>
          )}

          <form onSubmit={twoFaEnabled ? handleDisable : handleVerify}>
            <div className={styles.inputField}>
              <label className={styles.label}>Verification Code</label>
              <input
                type="text"
                className={styles.input}
                placeholder="Enter verification code"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                required
                maxLength={6}
              />
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={verify2FAMutation.isPending || disable2FAMutation.isPending}
              >
                {(verify2FAMutation.isPending || disable2FAMutation.isPending)
                  ? (twoFaEnabled ? 'Disabling...' : 'Enabling...')
                  : (twoFaEnabled ? 'Disable 2FA' : 'Enable 2FA')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TwoFAModal;
